import React from 'react';
import {
  Badge, Button, Col, Modal, Row, Table,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import BookingContextList from './BookingContextList';
import InvoiceStatusPill from './InvoiceStatusPill';
import DocumentTotal from './DocumentTotal';
import {
  DOC_TYPE_META, PAYMENT_METHODS, docTypeLabel, formatAmount, lineGross,
} from '../../Utils/billingoDocuments';

const dateTime = (v) => (v && dayjs(v).isValid() ? dayjs(v).format('D MMM YYYY HH:mm') : '');
const date = (v) => (v && dayjs(v).isValid() ? dayjs(v).format('D MMM YYYY') : '');

const paymentMethodLabel = (value) => (
  PAYMENT_METHODS.find((m) => m.value === value)?.label || value || ''
);

// One label/value pair in the document-info grid. Rendered only when there is
// something to show, so the grid never carries empty rows.
function Info({ label, value, muted = false }) {
  if (value == null || value === '') return null;
  return (
    <Col xs={6} md={4} className="mb-2">
      <div className="text-muted" style={{ fontSize: '.75rem' }}>{label}</div>
      <div className={`small ${muted ? 'text-muted' : 'fw-medium'} text-break`}>{value}</div>
    </Col>
  );
}

/**
 * Everything about one issued document, in one place: its fiscal metadata and
 * audit trail (including WHO issued it), the order(s) it bills, and the lines
 * it charges — plus the same actions the list row offers. `actions` come from
 * useDocumentActions, which decides them for the row too; `error` is its
 * alert, shown here because the list's own is behind this modal.
 *
 * Read-only: a fiscal document cannot be edited after issue, only sztornózva.
 */
function BillingoDocumentDetailsModal({
  doc = null,
  show = false,
  onHide,
  actions = [],
  error = null,
}) {
  if (!doc) return null;

  const isCancelled = doc.status === 'cancelled';
  const bookings = doc.bookings || [];
  const lines = doc.line_items || [];

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2 flex-wrap fs-5">
          <Badge
            bg={DOC_TYPE_META[doc.type]?.variant || 'secondary'}
            text={DOC_TYPE_META[doc.type]?.variant === 'light' ? 'dark' : undefined}
          >
            {docTypeLabel(doc.type)}
          </Badge>
          <span>{doc.number}</span>
          <InvoiceStatusPill status={doc.status} />
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error}
        <div className="mb-3">
          <DocumentTotal doc={doc} className="fs-4 fw-semibold" />
        </div>

        <h6 className="text-muted mb-2">Document info</h6>
        <Row className="g-0 mb-3">
          <Info label="Issued" value={dateTime(doc.created_at)} />
          {/* The audit answer to "who put this out?" — the Cognito user who
              issued it, or the system fallback for pre-attribution documents. */}
          <Info label="Created by" value={doc.created_by || 'Unknown'} muted={!doc.created_by} />
          <Info label="Fulfillment date" value={date(doc.fulfillment_date)} />
          <Info label="Due date" value={date(doc.due_date)} />
          <Info label="Payment method" value={paymentMethodLabel(doc.payment_method)} />
          <Info label="Payment date" value={date(doc.payment_date)} />
          <Info label="Billingo id" value={doc.billingo_document_id} />
          <Info
            label="Advances deducted"
            value={(doc.advance_invoice_ids || []).join(', ')}
          />
          {/* The other direction of the deduction, for an Előlegszámla: what
              SPENT this advance. Billingo keeps the link only on the child, so
              this is the only place the answer appears — and it is the answer
              to "why does this advance keep being refused?", which cost one
              order group nineteen attempts (2026-11-02). Absent, and so not
              rendered, while the advance is still spendable. */}
          <Info
            label="Spent by"
            value={doc.advance_consumption && (doc.advance_consumption.by === 'deducted'
              ? `Számla ${doc.advance_consumption.number || '(unnamed)'}`
                + `${doc.advance_consumption.at ? ` · ${date(doc.advance_consumption.at)}` : ''}`
              : 'a document Billingo holds and we do not'
                + `${doc.advance_consumption.at ? ` · refused ${date(doc.advance_consumption.at)}` : ''}`)}
          />
          <Info
            label="Last sent"
            value={doc.last_sent_at
              ? `${dateTime(doc.last_sent_at)} → ${(doc.last_sent_to || []).join(', ')}`
              : ''}
          />
          {isCancelled && (
            <Info
              label="Cancelled (sztornó)"
              value={[dateTime(doc.cancelled_at), doc.cancellation_billingo_document_id
                ? `· doc ${doc.cancellation_billingo_document_id}` : '']
                .filter(Boolean).join(' ') || 'yes'}
            />
          )}
        </Row>

        <h6 className="text-muted mb-2">
          {bookings.length > 1 ? `Orders (${bookings.length})` : 'Order'}
        </h6>
        {bookings.length === 0 ? (
          <div className="text-muted small mb-3">
            {(doc.order_references || []).length > 0
              ? `No order context available for ${(doc.order_references || []).join(', ')}.`
              : 'This document is not linked to an order.'}
          </div>
        ) : (
          <BookingContextList bookings={bookings} className="mb-3" />
        )}

        {lines.length > 0 && (
          <>
            <h6 className="text-muted mb-2">Lines</h6>
            <Table responsive size="sm" className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Description</th>
                  <th>Order</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Unit</th>
                  <th className="text-end">VAT</th>
                  <th className="text-end">Gross</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  // Invoice lines have no id of their own; they are immutable
                  // once issued, so the index is a stable key here.
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`${l.order_line_key || l.description}-${i}`}>
                    <td>
                      {l.description}
                      {l.comment && <div className="text-muted small">{l.comment}</div>}
                    </td>
                    <td className="small text-muted">{l.order_reference || '—'}</td>
                    <td className="text-end">{l.quantity}</td>
                    <td className="text-end">{formatAmount(l.unit_price, l.currency)}</td>
                    <td className="text-end">{`${l.vat}%`}</td>
                    <td className="text-end">
                      {formatAmount(l.gross != null ? l.gross : lineGross(l), l.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div className="d-flex gap-2 flex-wrap">
          {actions.map((action) => (
            <Button
              key={action.key}
              variant={`outline-${action.tone || 'secondary'}`}
              size="sm"
              title={action.title}
              disabled={action.disabled}
              onClick={action.run}
            >
              <i className={`fa ${action.icon} me-1`} />
              {action.label}
            </Button>
          ))}
        </div>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BillingoDocumentDetailsModal;
